import { buildSingleFrameUrl } from './AnimationUtilities';

export const initializeLoadingPipeline = (engine: any): void => {
    engine.runtimeFrames = engine.rawFrames.map((frame: any) => ({
        meta: frame,
        url: buildSingleFrameUrl(
            engine.subDir,
            frame.frameStem,
            engine.frameSize,
        ),
        isReady: false,
        image: null,
    }));

    if (engine.progressBar) {
        const expectedTotal = engine.performanceFrameSkip
            ? Math.ceil(engine.runtimeFrames.length / 2)
            : engine.runtimeFrames.length;
        engine.progressBar.max = expectedTotal;
    }

    executePreloadSequencer(engine, 0);
};

export const executePreloadSequencer = (engine: any, index: number): void => {
    if (index >= engine.runtimeFrames.length) {
        // Once buffering completes, restore stable targeted timeline metrics safely
        engine.fps = engine.initialDesiredFps;
        engine.frameInterval = 1000 / engine.fps;
        return;
    }

    if (engine.performanceFrameSkip && index % 2 !== 0) {
        executePreloadSequencer(engine, index + 1);
        return;
    }

    const targetFrame = engine.runtimeFrames[index];
    const img = new Image();

    img.onload = () => {
        targetFrame.isReady = true;

        // Only keep the image reference for frame 0 (needed for initial canvas sizing/render)
        if (index === 0) {
            targetFrame.image = img;
            if (!engine.isCanvasSized) {
                engine.sizeCanvasToLoadedAsset(img);
            }
            engine.renderFrameToCanvas(0);
        } else {
            // For all other frames, let img fall out of scope so WebKit frees the decoded buffer.
            // DO NOT set img.src = '' (that cancels network). Just leave targetFrame.image null.
            targetFrame.image = null;
        }

        engine.loadedCount++;

        if (engine.progressBar) {
            engine.progressBar.value = engine.loadedCount;
        }

        // Dynamic network throttling pace calculation
        if (engine.loadedCount < engine.runtimeFrames.length) {
            const totalElapsedMs = performance.now() - engine.loadingStartTime;
            const avgMsPerFrame = totalElapsedMs / engine.loadedCount;
            const safeNetworkFps = (1000 / avgMsPerFrame) * 0.85;

            const bufferRatio =
                engine.loadedCount / engine.runtimeFrames.length;
            let dynamicFps =
                safeNetworkFps +
                (engine.initialDesiredFps - safeNetworkFps) * bufferRatio;

            dynamicFps = Math.max(
                2,
                Math.min(engine.initialDesiredFps, dynamicFps),
            );

            engine.fps = dynamicFps;
            engine.frameInterval = 1000 / engine.fps;
        }

        executePreloadSequencer(engine, index + 1);
    };

    img.onerror = () => {
        console.error(
            `Asset failed connection lifecycle fallback: ${targetFrame.url}`,
        );
        // SAFETY NET: Count failed frames toward completion so progressBar completes
        targetFrame.isReady = true;
        targetFrame.image = null;
        engine.loadedCount++;

        if (engine.progressBar) {
            engine.progressBar.value = engine.loadedCount;
        }

        executePreloadSequencer(engine, index + 1);
    };

    img.src = targetFrame.url;
};
